import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const VehicleDamageSketch = ({ damagePoints = [], onDamagePointsChange, readOnly = false }) => {
  const [points, setPoints] = useState(Array.isArray(damagePoints) ? damagePoints : []);
  const [showModal, setShowModal] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    setPoints(Array.isArray(damagePoints) ? damagePoints : []);
  }, [damagePoints]);

  const damageTypes = [
    'Scratch', 'Dent', 'Crack', 'Rust', 'Paint Damage', 'Missing Part', 'Other'
  ];

  const [modalData, setModalData] = useState({ label: '', damageType: 'Scratch', color: '#ef4444' });

  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly || !imageLoaded || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;

    setModalData({ label: '', damageType: 'Scratch', color: '#ef4444' });
    setSelectedPoint({ xPercent, yPercent, label: '', damageType: 'Scratch', color: '#ef4444' });
    setShowModal(true);
  };

  const addDamagePoint = (pointData) => {
    const newPoints = [...(Array.isArray(points) ? points : []), pointData];
    setPoints(newPoints);
    onDamagePointsChange?.(newPoints);
    setShowModal(false);
    setSelectedPoint(null);
  };



  const removeDamagePoint = (index) => {
    const newPoints = (Array.isArray(points) ? points : []).filter((_, i) => i !== index);
    setPoints(newPoints);
    onDamagePointsChange?.(newPoints);
  };

  return (
    <div className="relative">
      <div className="vehicle-sketch-container relative inline-block w-full max-w-[384px]">
        {!imageLoaded && (
          <div className="w-full max-w-[384px] h-64 bg-gray-200 border rounded flex items-center justify-center">
            <span className="text-gray-500">Loading vehicle sketch...</span>
          </div>
        )}
        <img
          ref={imageRef}
          src="/sketch1.jpg"
          alt="Vehicle Damage Sketch"
          className={`w-full max-w-[384px] h-auto border rounded ${!readOnly ? 'cursor-crosshair' : ''} ${!imageLoaded ? 'hidden' : ''}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
          onClick={handleImageClick}
          onTouchEnd={(e) => {
            if (readOnly || !imageLoaded || !imageRef.current) return;
            e.preventDefault();
            const touch = e.changedTouches[0];
            const rect = imageRef.current.getBoundingClientRect();
            const xPercent = ((touch.clientX - rect.left) / rect.width) * 100;
            const yPercent = ((touch.clientY - rect.top) / rect.height) * 100;
            setModalData({ label: '', damageType: 'Scratch', color: '#ef4444' });
            setSelectedPoint({ xPercent, yPercent, label: '', damageType: 'Scratch', color: '#ef4444' });
            setShowModal(true);
          }}
        />

        {imageLoaded && (Array.isArray(points) ? points : []).map((point, index) => {
          // Use percentage-based positioning for consistency
          const xPercent = point.xPercent || (point.x || 0);
          const yPercent = point.yPercent || (point.y || 0);
          
          return (
            <div
              key={index}
              className="damage-marker absolute"
              style={{ 
                left: `${xPercent}%`,
                top: `${yPercent}%`,
                width: '18px',
                height: '18px',
                backgroundColor: point.color || '#ef4444',
                border: '2px solid #ffffff',
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                cursor: readOnly ? 'default' : 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}
              title={`${point.label} - ${point.damageType}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!readOnly) removeDamagePoint(index);
              }}
            >
              <span 
                className="damage-marker-label absolute"
                style={{
                  top: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  fontSize: '10px',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  whiteSpace: 'nowrap'
                }}
              >
                {index + 1}
              </span>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Damage Point</h3>
              <button onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Location Label</label>
                  <input
                    type="text"
                    value={modalData.label}
                    onChange={e => setModalData(p => ({ ...p, label: e.target.value }))}
                    placeholder="e.g., Front Left Door"
                    className="w-full px-3 py-2 border rounded-md"
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Damage Type</label>
                  <select value={modalData.damageType} onChange={e => setModalData(p => ({ ...p, damageType: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                    {damageTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Marker Color</label>
                  <select value={modalData.color} onChange={e => setModalData(p => ({ ...p, color: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                    <option value="#ef4444">Red</option>
                    <option value="#f59e0b">Yellow</option>
                    <option value="#3b82f6">Blue</option>
                    <option value="#10b981">Green</option>
                    <option value="#8b5cf6">Purple</option>
                    <option value="#f97316">Orange</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (modalData.label.trim()) {
                        addDamagePoint({
                          xPercent: selectedPoint.xPercent,
                          yPercent: selectedPoint.yPercent,
                          label: modalData.label.trim(),
                          damageType: modalData.damageType,
                          color: modalData.color
                        });
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Point
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDamageSketch;
